// Fullscreen mobilní vyhledávání (screens/m01-header.md §„Fullscreen vyhledávání",
// mockup design/HeaderMobil.dc.html). Aktivní jen ≤ $bp-md.
//
// Sheet NENÍ vlastní panel nad hlavičkou — je to nativní `div.search`, které CSS
// při otevření roztáhne přes celou obrazovku. Důvod: našeptávač `div.searchWhisperer`
// plní default JS jako sourozence formu, takže když sheet obalí celé `.search`,
// spadnou výsledky rovnou dovnitř a nemusíme sahat na nativní DOM ani na jeho JS.
//
// Dodáváme: zpět, mazání dotazu, prázdný stav (nejhledanější + kategorie) a CTA
// na plný výpis. Skin obsahu našeptávače v sheetu řeší _whisperer.scss (#31).

import { icon, iconHtml } from './icon.js';
import { CAT_ICONS, readMenu } from './menuModel.js';
import { POPULAR, searchUrl } from './searchSuggest.js';
import { catalogRoot } from './pages.js';
import L from '../locale/cs/mobileSearch.js';

const LABEL_BACK = L.back;
const LABEL_CLEAR = L.clear;
const LABEL_POPULAR = L.popular;
const LABEL_CATS = L.cats;
const LABEL_CTA = L.cta;

const MQ = '(max-width: 768px)';

// Kategorie v prázdném stavu — jen ty, které v nativním menu opravdu jsou.
const IDLE_CATS = ['garden', 'build', 'workshop', 'car'].map(catalogRoot).filter(Boolean);

let root = null; // div.search
let input = null;
let idle = null;
let cta = null;
let clearBtn = null;

function isMobile() {
  return window.matchMedia(MQ).matches;
}

function sync() {
  const q = input.value.trim();
  root.classList.toggle('has-query', q.length > 0);
  if (cta) cta.href = searchUrl(q);
}

const OPEN = 'bs-search-open';

// Scroll pozice stránky pod sheetem. `overflow: hidden` na body WebKit (iPhone —
// Safari i Chrome) při dotyku IGNORUJE: stránka pod sheetem jela dál, ukazovala
// vlastní scrollbar a její `scroll` eventy zavíraly panel našeptávače (nález
// Tomáše 2026-09-09). Body se proto po dobu sheetu FIXUJE (`position: fixed`
// v _mobile-search.scss) s odsazením `top: -scrollY`, po zavření se pozice vrátí.
let lockedY = 0;

export function isMobileSearchOpen() {
  return document.body.classList.contains(OPEN);
}

export function openMobileSearch() {
  if (!root || !isMobile() || isMobileSearchOpen()) return;
  lockedY = window.scrollY || 0;
  document.body.style.top = -lockedY + 'px';
  document.body.classList.add(OPEN);
  sync();
  if (input && document.activeElement !== input) input.focus();
}

export function closeMobileSearch() {
  if (!isMobileSearchOpen()) return;
  document.body.classList.remove(OPEN);
  document.body.style.top = '';
  window.scrollTo(0, lockedY);
  if (input) input.blur();
}

// Klávesnice pryč, jakmile se prst dotkne čehokoli mimo horní řádek s polem
// (scroll výsledků, tap na produkt / kategorii / chip): s klávesnicí je
// v sheetu vidět jeden a kousek produktu (zadání Tomáše 2026-09-09). Blur je
// programový a sheet zůstává otevřený — searchWhisperer.js v otevřeném sheetu
// na `focusout` ani na scroll panel nezavírá; klik do pole klávesnici vrátí.
function dismissKeyboard(e) {
  if (!input || document.activeElement !== input) return;
  const t = e.target;
  if (t && t.closest && t.closest('.search-form fieldset')) return;
  input.blur();
}

// --------------------------------------------------------------- markup

function eyebrow(text) {
  const el = document.createElement('div');
  el.className = 'bs-msearch__eyebrow';
  el.textContent = text;
  return el;
}

function buildIdle() {
  const wrap = document.createElement('div');
  wrap.className = 'bs-msearch__idle';

  wrap.appendChild(eyebrow(LABEL_POPULAR));
  const chips = document.createElement('div');
  chips.className = 'bs-msearch__chips';
  // mockup HeaderMobil má v sheetu 6 chipů (desktopová stránka hledání 7)
  for (const q of POPULAR.slice(0, 6)) {
    const a = document.createElement('a');
    a.className = 'bs-msearch__chip';
    a.href = searchUrl(q);
    a.textContent = q;
    chips.appendChild(a);
  }
  wrap.appendChild(chips);

  const byPath = {};
  for (const r of readMenu()) byPath[r.path] = r;
  const cats = IDLE_CATS.map(function (p) {
    return byPath[p];
  }).filter(Boolean);
  if (cats.length) {
    wrap.appendChild(eyebrow(LABEL_CATS));
    for (const cat of cats) {
      const a = document.createElement('a');
      a.className = 'bs-msearch__cat';
      a.href = cat.href;
      const circle = document.createElement('span');
      circle.className = 'bs-msearch__cat-icon';
      circle.appendChild(icon(CAT_ICONS[cat.path] || 'grid'));
      a.appendChild(circle);
      const label = document.createElement('span');
      label.className = 'bs-msearch__cat-label';
      label.textContent = cat.label;
      a.appendChild(label);
      a.insertAdjacentHTML('beforeend', iconHtml('chevron-down', 'bs-msearch__chev'));
      wrap.appendChild(a);
    }
  }
  return wrap;
}

function buildButton(className, iconName, label) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = className;
  btn.setAttribute('aria-label', label);
  btn.appendChild(icon(iconName));
  return btn;
}

// --------------------------------------------------------------- init

export function initMobileSearch() {
  root = document.querySelector('#header .search');
  if (!root || root.dataset.bsMsearch) return;
  input = root.querySelector('.query-input') || root.querySelector('input[type="search"]');
  const fieldset = root.querySelector('.search-form fieldset');
  if (!input || !fieldset) return;
  root.dataset.bsMsearch = '1';

  const back = buildButton('bs-msearch__back', 'arrow-left', LABEL_BACK);
  back.addEventListener('click', closeMobileSearch);
  fieldset.prepend(back);

  clearBtn = buildButton('bs-msearch__clear', 'close', LABEL_CLEAR);
  clearBtn.addEventListener('click', function () {
    input.value = '';
    // default JS našeptávače poslouchá na inputu → musí se dozvědět o smazání
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('keyup', { bubbles: true }));
    sync();
    input.focus();
  });
  // ✕ patří DOVNITŘ pole, před tlačítko „Hledat" (mockup SiteHeader, prop `dotaz`).
  // `appendChild` ho dával až za tlačítko, které má záporné okraje, aby lícovalo
  // s pravou hranou pilulky — křížek tak pilulku rozbil a tlačítko odsunul doleva.
  // Na mobilu je `.search-button` schované, takže ✕ i tak končí vpravo.
  const searchBtn = fieldset.querySelector('.search-button');
  if (searchBtn) fieldset.insertBefore(clearBtn, searchBtn);
  else fieldset.appendChild(clearBtn);

  idle = buildIdle();
  root.appendChild(idle);

  // CTA drží spodní hranu sheetu (v mockupu je za výsledky) — na mobilu je
  // pod prstem a nezmizí při scrollu dlouhého seznamu.
  cta = document.createElement('a');
  cta.className = 'bs-msearch__cta';
  cta.href = searchUrl('');
  cta.appendChild(document.createTextNode(LABEL_CTA));
  cta.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
  root.appendChild(cta);

  input.addEventListener('focus', openMobileSearch);
  fieldset.addEventListener('click', function (e) {
    if (e.target.closest('.bs-msearch__back, .bs-msearch__clear')) return;
    openMobileSearch();
  });
  input.addEventListener('input', sync);

  // dotyk mimo horní řádek + scroll kteréhokoli panelu sheetu (scroll nebublá →
  // capture) → klávesnice dolů; pasivní, aby tap i scroll zůstaly plynulé
  root.addEventListener('touchstart', dismissKeyboard, { passive: true });
  root.addEventListener('scroll', dismissKeyboard, { capture: true, passive: true });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeMobileSearch();
  });
  window.addEventListener('resize', function () {
    if (!isMobile()) closeMobileSearch();
  });

  sync();
}
